// src/oura/topSearches.ts
//
// What Google is actually asked about a business (ourteam-backend#4981, epic
// #4979), shared by the two screens that show it.
//
// ONE DEFINITION of the tier. The Visibility table, the uncovered card and the
// Search performance card all print the same chip, and a reader who learns
// "#4 most searched" on one screen must not meet a different sentence for the
// same rank on another.
//
// A RANK IS NOT A ROW NUMBER. The backend ranks judged searches by yearly
// volume after dropping branded, low-volume, out-of-band and out-of-market
// ones, so #4 means "the fourth most-searched thing worth acting on", not the
// fourth row Google returned.
//
// PAST 50 IS SHOWN, QUIETLY. It used to be hidden outright, on the rule that a
// search nobody makes is not a finding. That hid the wrong thing: a staffer
// looking at a question with a real Google search behind it saw nothing at all,
// and could not tell "no search reached this slot" from "one did, ranked 126th".
// It now carries a third, deliberately quiet chip that names the rank and
// claims no tier. What stays capped at 50 is the set itself: `uncoveredSearches`
// only ever offers ranks inside the fifty, because a search at #126 is not a
// candidate worth spending one of the fifty slots on.

import type { AeoSearchMatch, AeoSearchQuestions, AeoSearchRow } from "@service/aeo";

export type SearchTier = "top10" | "top50" | "beyond";

/** `null` only when there is no usable rank at all. */
export function searchTier(rank: number | null | undefined): SearchTier | null {
    if (typeof rank !== "number" || rank < 1) return null;
    if (rank <= 10) return "top10";
    if (rank <= 50) return "top50";
    return "beyond";
}

/** Inside the fifty the question set is built to cover. */
export function inTopFifty(rank: number | null | undefined): boolean {
    const tier = searchTier(rank);
    return tier === "top10" || tier === "top50";
}

/**
 * "#4 most searched", the same words at every rank.
 *
 * It said "Top 10 search #4", which a business owner read as their own Google
 * position, sitting beside a Position column that said #36 (usability test,
 * 14 Oct 2026). The rank is how often the search is MADE, so the label says
 * so. The tier is still on the chip: its bars and colour, and the tooltip.
 */
export function searchTierLabel(rank: number): string {
    return `#${rank} most searched`;
}

/** `12,431 / yr`, the unit every one of these rows is counted in. */
export function perYear(searches: number): string {
    return `${searches.toLocaleString("en-GB")} / yr`;
}

/**
 * The demo's slot numbers, pinned by row position.
 *
 * Fixture questions carry no slot number, so the demo pins the chips to the
 * first rows. Live rows always join on their real `n`. It lives HERE, with the
 * join it serves, so the Visibility table and the Search performance card
 * cannot disagree about which question owns a search.
 */
const DEMO_SLOTS = [27, 41, 12];

/** The slot a question occupies: its own `n`, or the demo pin at its position. */
export function questionSlot(q: { n?: number; slot_n?: number }, index: number): number | null {
    return q.n ?? q.slot_n ?? DEMO_SLOTS[index] ?? null;
}

/**
 * The best match per question slot, keyed by the question's `n`.
 *
 * A slot can own several real searches; the chip names the strongest and says
 * how many more, because a row of five chips is not information a reader can
 * use at a glance.
 */
export function matchesBySlot(block: AeoSearchQuestions | null | undefined): Map<number, AeoSearchMatch[]> {
    const by = new Map<number, AeoSearchMatch[]>();
    if (!block?.connected) return by;
    for (const m of block.matches ?? []) {
        if (searchTier(m.rank) === null) continue;
        const list = by.get(m.slot) ?? [];
        list.push(m);
        by.set(m.slot, list);
    }
    // `forEach`, not `for…of` over `.values()`: this tsconfig targets ES5 for
    // iterables and would need downlevelIteration to walk a Map that way.
    by.forEach((list) => list.sort((a, b) => a.rank - b.rank));
    return by;
}

/** The searches no question covers, strongest first. */
export function uncoveredSearches(block: AeoSearchQuestions | null | undefined): AeoSearchRow[] {
    if (!block?.connected) return [];
    return (block.searches ?? [])
        // Ranks inside the fifty only. Past that a search is worth NOTICING —
        // the chip says so — but not worth spending one of the fifty slots on,
        // and Bee Choo alone would add 110 rows nobody should act on.
        .filter((r) => r.coveredBy === null && inTopFifty(r.rank))
        .sort((a, b) => a.rank - b.rank);
}

/** Every judged search, strongest first — the Search performance card's table. */
export function judgedSearches(block: AeoSearchQuestions | null | undefined): AeoSearchRow[] {
    if (!block?.connected) return [];
    return [...(block.searches ?? [])].sort((a, b) => a.rank - b.rank);
}

/**
 * The scan carries no search block at all: absent, or `{connected: false}`.
 *
 * That is a scan taken BEFORE Search Console was connected (or before the
 * backend built the block), not a property with nothing to report. Connecting
 * the account does not rebuild a stored scan, so a staffer who has just
 * connected it sees an empty card with no reason and, because the pull control
 * lives on the card, no way to fix it. Both cards use this to keep their head
 * and the pull control up in exactly that state.
 *
 * `connected: true` with empty lists is NOT this: that block was built, and
 * `searchesAbsentSentence` says why it is empty.
 */
export function searchesNotBuilt(block: AeoSearchQuestions | null | undefined): boolean {
    return !block || !block.connected;
}

/** What the card says when {@link searchesNotBuilt} and staff can pull. */
export const SEARCHES_NOT_BUILT_SENTENCE =
    "This scan has no searches matched to the 50 questions yet. If Search Console was connected "
    + "after the scan ran, pull it again to build them. No AI engines are asked.";

/**
 * Why a question row has no search chip, said once for the whole scan.
 *
 * The question drawer shows the empty chip, a stated absence with a reason a
 * reader can hover. The question table does not: an empty chip on most rows
 * was noise, and when NO question has a search the notice above the table
 * says it once. The
 * wording is audience-neutral: the action to take lives in the notice above
 * the table and is staff-only.
 *
 * `notBuilt` is the one state staff can act on (pull Search Console again).
 */
export interface SearchEmpty {
    label: string;
    why: string;
    notBuilt: boolean;
}

export function searchEmptyState(block: AeoSearchQuestions | null | undefined): SearchEmpty {
    if (searchesNotBuilt(block)) {
        return {
            label: "No search data",
            why: "This scan has no Google search data matched to the questions. Search Console was not "
                + "connected, or was connected after the scan ran.",
            notBuilt: true,
        };
    }
    const absent = searchesAbsentSentence(block);
    if (absent) return { label: "No search data", why: absent, notBuilt: false };
    return {
        label: "No search matched",
        why: "No Google search matched this question. Google leaves out the rarest searches, "
            + "so this is unmeasured, not zero.",
        notBuilt: false,
    };
}

/**
 * Why the card has nothing to show, in one sentence (#3897,
 * ourteam-backend#5053).
 *
 * THREE DIFFERENT SITUATIONS USED TO LOOK IDENTICAL: a small property whose
 * every query is below the volume floor, a property where every search is
 * someone typing the business's own name, and a matcher that is simply broken —
 * which it was, until ourteam-backend#5050, and which shipped unnoticed for
 * weeks precisely because an empty card says nothing.
 *
 * Returns `null` when there is nothing to say, and the card then behaves
 * exactly as it does today: a scan from before the backend sent a reason has
 * nothing in it to tell those three apart, so inventing a sentence for it would
 * be the screen guessing.
 */
export function searchesAbsentSentence(block: AeoSearchQuestions | null | undefined): string | null {
    if (!block?.connected) return null;
    if ((block.searches ?? []).length > 0) return null;
    const key = block.searchesAbsent;
    if (!key) return null;
    // The backend's own words win. It knows which counts it acted on, and a
    // rewrite here would be the screen restating a diagnosis it did not make.
    if (block.searchesAbsentDetail) return block.searchesAbsentDetail;

    const seen = typeof block.rowsSeen === "number" ? block.rowsSeen : null;
    const floor = typeof block.searchFloor === "number" ? block.searchFloor : null;
    const d = block.dropped ?? null;
    const n = (v: number) => v.toLocaleString("en-GB");

    switch (key) {
        case "no_rows":
            return "Search Console has no queries for this site in the last 12 months yet.";
        case "all_branded":
            return "Every search Google recorded was someone typing the business's own name. "
                + "Those say nothing about what the 50 questions miss.";
        case "all_below_floor":
            // The floor is quoted from the payload, never from a constant here:
            // when the backend moves it, this sentence moves with it.
            return seen !== null && floor !== null
                ? `Search Console returned ${n(seen)} queries, but none were searched at least `
                  + `${n(floor)} times in the year — not enough volume to compare against the 50 questions yet.`
                : "None of Search Console's queries were searched often enough in the year to "
                  + "compare against the 50 questions yet.";
        case "all_out_of_band":
            // No position numbers: the band is the backend's, it is not in this
            // payload, and a figure invented here would be a threshold nobody set.
            return "Every query either already ranks at the top or sits too far down the results to act on.";
        case "all_out_of_market":
            return "Every query named a town this business does not trade in.";
        case "mixed": {
            const parts: string[] = [];
            if (d?.belowFloor) parts.push(`${n(d.belowFloor)} below the volume floor`);
            if (d?.branded) parts.push(`${n(d.branded)} branded`);
            if (d?.outOfBand) parts.push(`${n(d.outOfBand)} outside the ranking band`);
            if (d?.outOfMarket) parts.push(`${n(d.outOfMarket)} outside this market`);
            const head = seen !== null ? `None of Search Console's ${n(seen)} queries qualified` : "No query qualified";
            return parts.length > 0 ? `${head}: ${parts.join(", ")}.` : `${head}.`;
        }
        default:
            // A key this build does not know is still a stated reason. Saying so
            // beats printing the raw key at a client, and beats an empty card.
            return "Search Console returned nothing this card can compare against the 50 questions yet.";
    }
}
